import { useState } from 'react';
import { createFileRoute, Outlet, useChildMatches } from '@tanstack/react-router';
import { DetailPageLayout } from '@/components/layouts';
import { Fullbleed } from '@/contexts/fullbleed';
import { cn } from '@/lib/utils';
import { useHasPermission } from '@/hooks/usePermissions';
import { AgentPermissionsSection } from '@/components/organisms/settings-call/AgentPermissionsSection';
import { AutomationSection } from '@/components/organisms/settings-call/AutomationSection';
import { CallOutcomesSection } from '@/components/organisms/settings-call/CallOutcomesSection';
import { CallFlowsSection } from '@/components/organisms/settings-call/call-flows/CallFlowsSection';
import { IvrDirectoryPanel } from '@/components/organisms/settings-call/ivr-directory-panel';
import { LandlineSection } from '@/components/organisms/settings-call/LandlineSection';
import { WorkingHoursSection } from '@/components/organisms/settings-call/WorkingHoursSection';
import { NumbersTab } from '@/components/organisms/settings-call/number/NumbersTab';
import { IrisPanel } from '@/components/organisms/settings-call/team/IrisPanel';
import { SettingsTabBar } from '@/components/organisms/settings-call/ui';
import {
  DEFAULT_TAB,
  resolveTab,
  visibleTabs,
  type CallSettingsTab,
} from '@/components/organisms/settings-call/call-settings-tabs';
import { deriveDeployment } from '@/version';

export const Route = createFileRoute('/_dashboard/dashboard/settings/call-settings')({
  // The tab lives in the URL so it survives refresh, deep links and back/forward.
  // `number` opens that row on the Numbers tab. Retired tab names map to where
  // their contents went (see `resolveTab`).
  validateSearch: (search: Record<string, unknown>): { tab: CallSettingsTab; number?: string } => ({
    tab: resolveTab(search.tab),
    ...(typeof search.number === 'string' && search.number ? { number: search.number } : {}),
  }),
  component: CallSettingsPage,
});

const ID = 'call-settings';

const TABS = visibleTabs(deriveDeployment(import.meta.env.MODE));

function CallSettingsPage() {
  const search = Route.useSearch();
  const { number } = search;
  // A tab this deployment doesn't show (Iris on production) opens General.
  const tab = TABS.some((t) => t.value === search.tab) ? search.tab : DEFAULT_TAB;
  const navigate = Route.useNavigate();
  const canEdit = useHasPermission('company:edit');
  const canViewAgents = useHasPermission('users:view');
  // A tab is built the first time it is opened and kept after that: the page
  // loads light (Call flows alone draws a canvas per flow), and a half-edited
  // greeting still survives a trip to another tab.
  const [visited, setVisited] = useState<ReadonlySet<CallSettingsTab>>(() => new Set([tab]));
  if (!visited.has(tab)) setVisited(new Set([...visited, tab]));

  // The flow builder is a child route and owns the whole content area.
  const childMatches = useChildMatches();
  if (childMatches.length > 0) return <Outlet />;

  // `replace` keeps tab switches out of history: back leaves the page.
  const setTab = (next: CallSettingsTab) => {
    if (next !== tab) void navigate({ search: { tab: next }, replace: true });
  };
  const setOpenNumber = (id: string | undefined) =>
    void navigate({ search: { tab: 'numbers', ...(id ? { number: id } : {}) }, replace: true });

  const panel = (value: CallSettingsTab, children: React.ReactNode) =>
    visited.has(value) && (
      <div
        role="tabpanel"
        id={`${ID}-panel-${value}`}
        aria-labelledby={`${ID}-tab-${value}`}
        className={cn(tab !== value && 'hidden')}
      >
        {children}
      </div>
    );

  return (
    <>
      <Fullbleed />
      <DetailPageLayout
        header={
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold">Call settings</h1>
            <p className="text-xs text-muted-foreground">
              How your business answers, routes and places calls.
            </p>
          </div>
        }
        contentClassName="px-5 pb-12 pt-0 [scrollbar-gutter:stable] md:px-10 xl:px-14"
      >
        <div className="mx-auto w-full max-w-[960px]">
          <div className="sticky top-0 z-20 bg-background pt-3">
            <SettingsTabBar tabs={TABS} value={tab} onChange={setTab} idPrefix={ID} />
          </div>
          <div className="pt-6">
            {panel(
              'general',
              <div className="space-y-8">
                <AutomationSection />
                <WorkingHoursSection />
              </div>,
            )}
            {panel('numbers', <NumbersTab openId={number} onOpenChange={setOpenNumber} />)}
            {panel('flows', <CallFlowsSection canEdit={canEdit} />)}
            {panel(
              'team',
              <div className="space-y-8">
                {canViewAgents && <AgentPermissionsSection />}
                <IvrDirectoryPanel />
              </div>,
            )}
            {panel('desk-phones', <LandlineSection />)}
            {panel('outcomes', <CallOutcomesSection />)}
            {TABS.some((t) => t.value === 'iris') && panel('iris', <IrisPanel />)}
          </div>
        </div>
      </DetailPageLayout>
    </>
  );
}
